import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import TimeColumn from "./TimeColumn";
import DayColumn from "./DayColumn";
import { DAYS, HOURS } from "./constants";
// Initialize schedule with all slots selected by default
const createInitialSchedule = (initialSchedule) => {
  const schedule = {};
  DAYS.forEach((day) => {
    schedule[day] = {};
    HOURS.forEach((hour) => {
      schedule[day][hour] = initialSchedule?.[day]?.[hour] ?? true;
    });
  });
  return schedule;
};
const WeeklyScheduleDialog = React.memo(
  ({ open, onClose, onSave, initialSchedule }) => {
    const [schedule, setSchedule] = useState(() =>
      createInitialSchedule(initialSchedule)
    );
    const [isDragging, setIsDragging] = useState(false);
    const [dragValue, setDragValue] = useState(false);

    // Reset schedule when dialog opens with new initialSchedule
    React.useEffect(() => {
      if (open) {
        setSchedule(createInitialSchedule(initialSchedule));
      }
    }, [open, initialSchedule]);

    // Memoized helper to check if all hours in a day are selected
    const isAllDaySelected = useCallback(
      (day) => {
        return HOURS.every((hour) => schedule[day][hour]);
      },
      [schedule]
    );

    // Toggle a single cell
    const toggleCell = useCallback((day, hour) => {
      setSchedule((prev) => ({
        ...prev,
        [day]: {
          ...prev[day],
          [hour]: !prev[day][hour],
        },
      }));
    }, []);

    // Start dragging
    const handleMouseDown = useCallback(
      (day, hour) => {
        const newValue = !schedule[day][hour];
        setDragValue(newValue);
        setIsDragging(true);
        toggleCell(day, hour);
      },
      [schedule, toggleCell]
    );

    // Continue dragging
    const handleMouseEnter = useCallback(
      (day, hour) => {
        if (isDragging && schedule[day][hour] !== dragValue) {
          setSchedule((prev) => ({
            ...prev,
            [day]: {
              ...prev[day],
              [hour]: dragValue,
            },
          }));
        }
      },
      [isDragging, dragValue, schedule]
    );

    // Stop dragging
    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Select all slots
    const handleSelectAll = useCallback(() => {
      const newSchedule = {};
      DAYS.forEach((day) => {
        newSchedule[day] = Object.fromEntries(
          HOURS.map((hour) => [hour, true])
        );
      });
      setSchedule(newSchedule);
    }, []);

    // Clear all slots
    const handleClearAll = useCallback(() => {
      const newSchedule = {};
      DAYS.forEach((day) => {
        newSchedule[day] = Object.fromEntries(
          HOURS.map((hour) => [hour, false])
        );
      });
      setSchedule(newSchedule);
    }, []);

    // Toggle all hours in a day
    const toggleAllDay = useCallback(
      (day) => {
        const newValue = !isAllDaySelected(day);
        setSchedule((prev) => ({
          ...prev,
          [day]: Object.fromEntries(HOURS.map((hour) => [hour, newValue])),
        }));
      },
      [isAllDaySelected]
    );

    // Save and close
    const handleSave = useCallback(() => {
      onSave(schedule);
      onClose();
    }, [schedule, onSave, onClose]);
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            maxWidth: "900px",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 3,
          }}
        >
          {/* Header Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                startIcon={<CheckBoxIcon />}
                onClick={handleSelectAll}
                sx={{
                  color: "#212121",
                  textTransform: "none",
                  backgroundColor: "#f5f5f5",
                  px: 2,
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                Select All
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                onClick={handleClearAll}
                sx={{
                  color: "#212121",
                  textTransform: "none",
                  backgroundColor: "#f5f5f5",
                  px: 2,
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {/* Schedule Grid */}
          <Box
            sx={{
              display: "flex",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
              userSelect: "none",
            }}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Time Column */}
            <TimeColumn />

            {/* Days Columns */}
            {DAYS.map((day, dayIndex) => (
              <DayColumn
                key={day}
                day={day}
                dayIndex={dayIndex}
                schedule={schedule[day]}
                isAllSelected={isAllDaySelected(day)}
                onToggleAllDay={() => toggleAllDay(day)}
                onMouseDown={handleMouseDown}
                onMouseEnter={handleMouseEnter}
                isLastDay={dayIndex === DAYS.length - 1}
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#212121",
              textTransform: "none",
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#2932E5",
              color: "#fff",
              textTransform: "none",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1f28c7",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
WeeklyScheduleDialog.displayName = "WeeklyScheduleDialog";
export default WeeklyScheduleDialog;
